import React from "react";
import { useUnifiedService } from "../../utilities/contexts/UnifiedServiceContext";
import { ServiceStep } from "../../utilities/types/unified-services";
import { getServiceStepFlow } from "../../utilities/helpers/serviceStepMapper";

interface NavigationConfig {
  showPrevious?: boolean;
  showNext?: boolean;
  previousText?: string;
  nextText?: string;
  previousDisabled?: boolean;
  nextDisabled?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
}

interface UnifiedStepNavigationProps {
  config?: NavigationConfig;
}

const UnifiedStepNavigation: React.FC<UnifiedStepNavigationProps> = ({ config }) => {
  const { serviceInterface, serviceConfig } = useUnifiedService();
  const { currentStep, canProceedToNextStep, setCurrentStep } = serviceInterface;

  const stepFlow = getServiceStepFlow(serviceConfig);

  const [guideSlideIndex, setGuideSlideIndex] = React.useState(0);
  const isGuideStep = currentStep === ServiceStep.GUIDE;
  const guideSlides = serviceConfig.guide.slides;
  const totalGuideSlides = guideSlides.length;

  const handleNext = () => {
    if (isGuideStep && totalGuideSlides > 1) {
      if (guideSlideIndex < totalGuideSlides - 1) {
        const nextSlide = guideSlideIndex + 1;
        setGuideSlideIndex(nextSlide);

        const event = new CustomEvent("guideSlideChange", { detail: { slideIndex: nextSlide } });
        window.dispatchEvent(event);
        return;
      }
    }

    const nextStep = stepFlow.getNextStep(currentStep);
    if (nextStep !== null) {
      setCurrentStep(nextStep);
      setGuideSlideIndex(0);
    }
  };

  const handlePrevious = () => {
    if (isGuideStep && totalGuideSlides > 1 && guideSlideIndex > 0) {
      const prevSlide = guideSlideIndex - 1;
      setGuideSlideIndex(prevSlide);

      const event = new CustomEvent("guideSlideChange", { detail: { slideIndex: prevSlide } });
      window.dispatchEvent(event);
      return;
    }

    const previousStep = stepFlow.getPreviousStep(currentStep);
    if (previousStep !== null) {
      setCurrentStep(previousStep);
    }
  };

  // Reset guide slide index when entering guide step
  React.useEffect(() => {
    if (isGuideStep) {
      setGuideSlideIndex(0);
      // Sync with guide component
      const event = new CustomEvent("guideSlideChange", { detail: { slideIndex: 0 } });
      window.dispatchEvent(event);
    }
  }, [isGuideStep]);

  // Listen for guide slide updates from the guide component
  React.useEffect(() => {
    const handleGuideSlideUpdate = (event: any) => {
      const { slideIndex } = event.detail;
      setGuideSlideIndex(slideIndex);
    };

    window.addEventListener("guideSlideUpdate", handleGuideSlideUpdate);
    return () => {
      window.removeEventListener("guideSlideUpdate", handleGuideSlideUpdate);
    };
  }, []);
  const isFirstStep = currentStep === ServiceStep.GUIDE && (!isGuideStep || guideSlideIndex === 0);
  const isLastStep = currentStep === ServiceStep.RESULTS;

  const getNextText = () => {
    if (isGuideStep && totalGuideSlides > 1) {
      if (guideSlideIndex < totalGuideSlides - 1) {
        return "Next Guide";
      } else {
        return "Start Process";
      }
    }
    return currentStep === ServiceStep.HEIGHT_INPUT ? "Start Processing" : "Next";
  };

  const getPreviousText = () => {
    if (isGuideStep && totalGuideSlides > 1 && guideSlideIndex > 0) {
      return "Previous Guide";
    }
    return "Previous";
  };

  const isNextDisabled = () => {
    if (isGuideStep && totalGuideSlides > 1) {
      return false;
    }
    return !canProceedToNextStep || isLastStep;
  };

  const isPreviousDisabled = () => {
    return isFirstStep;
  };

  const defaultConfig: NavigationConfig = {
    showPrevious: !isFirstStep,
    showNext: !isLastStep,
    previousText: getPreviousText(),
    nextText: getNextText(),
    previousDisabled: isPreviousDisabled(),
    nextDisabled: isNextDisabled(),
    onPrevious: handlePrevious,
    onNext: handleNext,
  };

  const finalConfig = { ...defaultConfig, ...config };

  if (currentStep === ServiceStep.PROCESSING || currentStep === ServiceStep.RESULTS) {
    if (!config) return null;
  }

  return (
    <div className="flex justify-center w-full">
      <div className="w-full px-4 py-3 bg-white rounded-xl">
        {finalConfig.showPrevious && finalConfig.showNext ? (
          <div className="flex items-center justify-between w-full gap-4">
            <button
              onClick={finalConfig.onPrevious}
              disabled={finalConfig.previousDisabled}
              className={` py-2 md:py-3 md:px-6 px-6 md:text-sm text-xs font-medium transition-all duration-200 border rounded-xl
                ${finalConfig.previousDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"}`}
            >
              {finalConfig.previousText}
            </button>
            <button
              onClick={finalConfig.onNext}
              disabled={finalConfig.nextDisabled}
              className={` py-2 md:py-3 md:px-6 px-6 md:text-sm text-xs font-medium transition-all duration-200 border rounded-xl
                ${finalConfig.nextDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-primary bg-primary text-white hover:bg-primary/90"}`}
            >
              {finalConfig.nextText}
            </button>
          </div>
        ) : finalConfig.showNext ? (
          <div className="flex justify-center">
            <button
              onClick={finalConfig.onNext}
              disabled={finalConfig.nextDisabled}
              className={`py-2 md:py-3 md:px-6 px-6 md:text-sm text-xs font-medium transition-all duration-200 border rounded-xl md:w-[21.875rem] 
                ${finalConfig.nextDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-primary bg-primary text-white hover:bg-primary/90"}`}
            >
              {finalConfig.nextText}
            </button>
          </div>
        ) : finalConfig.showPrevious ? (
          <div className="flex justify-center">
            <button
              onClick={finalConfig.onPrevious}
              disabled={finalConfig.previousDisabled}
              className={`py-2 md:py-3 md:px-6 px-6 md:text-sm text-xs font-medium transition-all duration-200 border rounded-xl md:w-[21.875rem] 
                ${finalConfig.previousDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"}`}
            >
              {finalConfig.previousText}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UnifiedStepNavigation;
